import { ChangeDetectorRef, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/se.platform.base';
import { IDataSource, IDataSourceItem } from '@se/cp.common';
import { DataBinding } from '@se/se.platform.base';
import { Logger } from '@se/se.platform.base';
import { first, flatten, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { CpComboControl, ICpEntity, isEntity } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CpDatasetService } from '../../cp-dataset-service';
import { cpAskClinicalEvaluationMessage, cpClinicalEvaluationMessage } from '../../store/cp.actions';
import { AbstractControlComponent } from '../abstract-control';

@Component({
    selector: 'cp-combo',
    templateUrl: './combo.component.html',
    styleUrls: ['./combo.component.scss'],
    standalone: false
})
export class CpComboComponent extends AbstractControlComponent {
    control!: CpComboControl;
    model: ICpEntity[] = [];

    comboboxFilter$ = new BehaviorSubject<string>('');

    private fullDataSource$ = new BehaviorSubject<IDataSource>([]);
    private fullDynamicDataSource$ = new BehaviorSubject<{ id: string; value: string }[]>([]);

    isModelArray = false;

    dataSource$ = combineLatest([this.fullDataSource$, this.comboboxFilter$]).pipe(
        takeUntil(this.destroyed$),
        map(([data, filter]) => {
            if (!filter) {
                return data;
            }

            return data.filter(
                (d) => d.Description && d.Description.toLocaleLowerCase().indexOf(filter.toLocaleLowerCase()) !== -1,
            );
        }),
    );

    dynamicDataSource$ = combineLatest([this.fullDynamicDataSource$, this.comboboxFilter$]).pipe(
        takeUntil(this.destroyed$),
        map(([data, filter]) => {
            if (!filter) {
                return data;
            }

            return data.filter(
                (d) => d.value && d.value.toLocaleLowerCase().indexOf(filter.toLocaleLowerCase()) !== -1,
            );
        }),
    );

    hasManyOptions$ = combineLatest([this.fullDataSource$, this.fullDynamicDataSource$]).pipe(
        map(([items, dynamicItems]) => items.length > 5 || dynamicItems.length > 5),
    );

    constructor(
        private configuratorService: SeConstraintConfiguratorService,
        private constraintFilterService: SeConstraintApplierService,
        private datasourceService: CpDatasourceService,
        private modelService: CpModelService,
        private datasetService: CpDatasetService,
        private actions$: Actions,
        private store: Store,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    compareItems(o1: ICpEntity | IDataSourceItem, o2: ICpEntity | IDataSourceItem) {
        if (!o1 || !o2) {
            return o1 === o2;
        }
        return o1.Id === o2.Id;
    }

    compareDynamicItems(o1: string, o2: string) {
        return o1 === o2;
    }

    getComponentTooltipText() {
        if (this.model?.length === 0 && this.control.tooltip && !this.control.icon) {
            return this.control.tooltip;
        }

        return '';
    }

    getOptionDescription(option: IDataSourceItem) {
        return (option.getPropertyPrimitiveValue('Description') ?? '') as string;
    }

    getDynamicOptionDescription(option: ICpEntity): string {
        return option.getPropertyPrimitiveValue('label') as string;
    }

    getTriggerText(): string {
        return Array.isArray(this.model)
            ? this.model?.map((item) => item.getPropertyValue('Description')).join(', ') ?? ''
            : this.model;
    }

    getTriggerTooltipText() {
        return Array.isArray(this.model)
            ? this.model?.map((item) => item.getPropertyValue('Description')).join('\n') ?? ''
            : this.model;
    }

    onFilter(text: string) {
        this.comboboxFilter$.next(text);
    }

    onFocus() {
        this.focusin.emit();
    }

    /*nel caso */
    onModelChange(model: ICpEntity | ICpEntity[] | null): boolean {
        let actualModel = model;

        if (this.isModelArray && !(model instanceof Array)) {
            if (model === null) {
                actualModel = [];
            } else {
                actualModel = [model];
            }
        } else if (!this.isModelArray && model instanceof Array) {
            actualModel = model[0];
        }

        const oldValue = this.modelService.getValue(this.control.dataBinding);

        const changed = this.control.onModelChange(this.modelService, actualModel, !this.isDisabled);

        this.cdr.detectChanges();

        /*90920 e 95428*/
        if (oldValue === null && Array.isArray(actualModel) && actualModel.length === 0) {
            return false;
        }

        return !!changed;
    }

    onReset($event: MouseEvent) {
        $event.stopPropagation();
        this.formControl?.setValue(this.isModelArray ? [] : null);
        this.comboboxFilter$.next('');
    }

    protected handleFilterNotification(constraintId: string) {
        this.entity?.select();
        this.fullDataSource$.next(this.constraintFilterService.applyFilter(constraintId) as IDataSource);
    }

    protected init() {
        this.control.dataSourceService = this.datasourceService;
        this.control.modelService = this.modelService;
        this.dataBind();
        this.listenToClinicalEvalMessage();
        this.askClinicalEvalMessage();
    }

    private askClinicalEvalMessage() {
        this.store.dispatch(cpAskClinicalEvaluationMessage({ binding: this.getIndexedDataBinding() }));
    }

    private listenToClinicalEvalMessage() {
        this.actions$
            .pipe(
                ofType(cpClinicalEvaluationMessage),
                takeUntil(this.destroyed$),
                filter((value) => {
                    const { binding } = value;
                    const actualBinding = this.getIndexedDataBinding();
                    return binding === actualBinding;
                }),
                map((value) => {
                    const { warning, suggestion } = value;
                    let message: string | null = null;
                    if (!!warning) message = `__CLINICAL_EVAL_MESSAGE__WARNING__${warning}`;
                    else if (!!suggestion) message = `__CLINICAL_EVAL_MESSAGE__SUGGESTION__${suggestion}`;
                    return message;
                }),
            )
            .subscribe((message) => {
                this.warningMessage = !!message ? message : null;
            });
    }

    private getIndexedDataBinding = (): string => {
        const parentBinding = this.control.dataBinding.entity;
        const parent = this.modelService.getEntity(parentBinding);
        const propertyName = this.control.dataBinding.propertyName;
        return parent?.path?.addSimple(propertyName).toString() ?? '';
    };

    private dataBind() {
        if (!this.control.dataBinding || !this.control.dataSource) {
            return;
        }

        this.control
            .getEntityObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(this._entitySubject);

        const initialValue = this.control.getInitialValue();

        /** bug 95428 */
        this.isModelArray = this.datasetService.getControlDataType(this.control.dataBinding) === 'GuidArray';

        // TODO: (34.10) spostare la gestione all'interno del _control_
        if (this.control.isDynamicDataSource) {
            const binding = new DataBinding(this.control.dataSource);
            this.modelService
                .registerEntity(binding)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((entity) => {
                    if (!entity) {
                        return;
                    }
                    const node = this.datasetService.getNode(binding);
                    const propertyValueName = node?.sourceDisplayProperty || 'Id';
                    const optionsIdString =
                        entity.getAllEntities(new DataBinding())?.map((e) => {
                            const value = e.getPropertyValue(propertyValueName) as string;
                            const id = e.getPropertyValue('Id') as string;
                            return { id, value };
                        }) || [];

                    this.fullDynamicDataSource$.next(optionsIdString);
                });
        } else {
            let initialValueForDataSource: ICpEntity[] = [];
            if (initialValue instanceof Array) {
                initialValueForDataSource = [...initialValue].filter(isEntity);
            } else if (isEntity(initialValue)) {
                initialValueForDataSource = [initialValue];
            }

            this.fullDataSource$.next(
                this.datasourceService.getById(
                    this.control.dataSource,
                    initialValueForDataSource.map((e) => e) as any,
                    this.control.queries,
                ) || [],
            );
        }

        this.formControl = new FormControl(initialValue, [
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required),
        ]);

        // Databinding effettivo del modello.
        if (this.control.isDynamicDataSource) {
            this.control
                .getModelDynamicObservable()
                .pipe(takeUntil(this.destroyed$))
                .subscribe((model: any) => {
                    let valueToSet = model;
                    this.formControl?.setValue(valueToSet);
                    this.model = model;
                });
        } else {
            this.control
                .getModelObservable()
                .pipe(takeUntil(this.destroyed$))
                .subscribe((model: ICpEntity[]) => {
                    let valueToSet = this.control.isMultiple ? model : first(model) || null;
                    this.formControl?.setValue(valueToSet);
                    this.model = model;
                });
        }

        const constraints = uniq(
            flatten(
                this.configuratorService.constraintSources
                    .getSourcesByListName(this.control.dataSource)
                    .map((s) => s.constraints),
            ),
        );

        const dataBindingContext = 'dataBinding' in this.control ? this.control.dataBinding.toString() : undefined;

        for (const c of constraints) {
            this.constraintNotifier
                .register(c.id, dataBindingContext)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((_) => this.handleFilterNotification(c.id));
        }
    }
}
